import { LoadingSpinner } from '@components';
import { useAppDispatch } from '@hooks/redux-custom';
import { AvaStore, AvaSuburbs, JimboResponse, StoreRanking, StoreRankingWithID } from '@models';
import { Autocomplete, Button, Dialog, DialogContent, Grid, IconButton, Paper, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useGetStoresListNoCacheQuery, useGetSuburbNoCacheQuery, useUpdateSuburbMutation } from '@services';
import { showToast } from '@store/slices/notification';
import { FC, useEffect, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  searchSuburb: string;
};

export const SuburbEditModal: FC<Props> = ({ isOpen, onClose, searchSuburb }) => {
  const { data: stores, isLoading: storesLoading } = useGetStoresListNoCacheQuery();
  const {
    data: AvaSuburb,
    isLoading: suburbLoading,
    refetch,
  } = useGetSuburbNoCacheQuery({
    suburb: searchSuburb,
  });
  const [store, setStore] = useState('');
  const [options, setOptions] = useState<AvaStore[]>([]);
  const [rows, setRows] = useState<StoreRankingWithID[]>([]);
  const [updatedStores, setStores] = useState<StoreRanking[]>([]);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [updateSuburb] = useUpdateSuburbMutation();
  const dispatch = useAppDispatch();
  const [gridKey, setGridKey] = useState<number>(0);

  const getStoreName = useMemo(() => {
    return (storeCode: string): string => {
      const store = stores?.find((store) => store.store_code === storeCode);
      return store?.name || 'N/A';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores]);

  const columns: GridColDef[] = [
    { field: 'store_code', headerName: 'Store Code', flex: 1 },
    {
      field: 'store_name',
      headerName: 'Store Name',
      flex: 2,
      renderCell: (params) => {
        const storeName = params.row.store_name || getStoreName(params.row.store_code);
        return storeName;
      },
    },
    { field: 'rank', headerName: 'Ranking', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => {
        return (
          <IconButton onClick={() => handleRemoveStore(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        );
      },
    },
  ];

  useEffect(() => {
    if (AvaSuburb && AvaSuburb.store_ranking) {
      setRows(
        AvaSuburb.store_ranking.map((row) => ({
          id: row.store_code,
          ...row,
        })),
      );

      setStores(AvaSuburb.store_ranking);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (stores) {
      const validStores = stores.filter((store) => store.store_code && !isNaN(Number(store.store_code))); // Filter out stores without store_code or with NaN store_code

      setOptions(validStores);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddStore = (storeCode: string) => {
    if (storeCode) {
      const isStoreExists = rows.some((row) => row.store_code === parseInt(storeCode));

      if (!isStoreExists) {
        const newStore: StoreRankingWithID = {
          id: parseInt(storeCode),
          store_name: getStoreName(storeCode),
          store_code: parseInt(storeCode),
          rank: rows.length + 1,
        };
        const newStoreWithoutID: StoreRanking = { store_code: parseInt(storeCode), rank: rows.length + 1 };

        setRows((prevRows) => [...prevRows, newStore]);
        setStores((prevRows) => [...prevRows, newStoreWithoutID]);
        setUnsavedChanges(true);
      }
    }
  };

  const handleRemoveStore = (storeCode: string) => {
    if (storeCode) {
      const updatedRows = rows.filter((row) => String(row.store_code) !== String(storeCode));
      const updatedStore = updatedStores.filter((store) => String(store.store_code) !== String(storeCode));

      setRows(updatedRows.map((row, index) => ({ ...row, rank: index + 1 })));
      setStores(updatedStore.map((row, index) => ({ ...row, rank: index + 1 })));
      setUnsavedChanges(true);
    }
  };

  const handleSave = () => {
    const rowData: StoreRanking[] = updatedStores;
    const listsToUpdate: Partial<AvaSuburbs>[] = [];

    listsToUpdate.push({
      key: AvaSuburb?.key,
      suburb_name: AvaSuburb?.suburb_name,
      timezone: AvaSuburb?.timezone,
      suburb_state: AvaSuburb?.suburb_state,
      suburb_postcode: AvaSuburb?.suburb_postcode ? AvaSuburb?.suburb_postcode?.toString() : '0000',
      store_ranking: rowData,
    });

    listsToUpdate &&
      updateSuburb(listsToUpdate)
        .unwrap()
        .then((response) => {
          refetch();
          setGridKey((prevKey) => prevKey + 1);
          setUnsavedChanges(false);

          dispatch(
            showToast({
              message: response,
              type: 'success',
            }),
          );
        })
        .catch((error: { data: JimboResponse }) => {
          console.log(error);

          dispatch(
            showToast({
              message: 'Failed to update stores!',
              type: 'error',
            }),
          );

          setUnsavedChanges(true);
        });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md" data-testid="suburb-edit-modal">
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', pb: 0, mt: 2 }}>
        {(storesLoading || suburbLoading) && <LoadingSpinner isOpen={storesLoading || suburbLoading} />}
        {stores && (
          <>
            <Grid container alignItems="center" spacing={2} py={2}>
              <Grid item xs={10}>
                <Autocomplete
                  id="store-search"
                  data-testid="store-searchbar"
                  onChange={(e: any, newValue: AvaStore | null) => {
                    setStore(newValue?.store_code ?? '');
                  }}
                  options={options}
                  getOptionLabel={(option) => `${option?.name} ${option?.store_code}` ?? ''}
                  disablePortal
                  renderInput={(params) => <TextField {...params} label="Search by store name or store code" />}
                />
              </Grid>
              <Grid item>
                <Button variant="contained" type="submit" onClick={() => handleAddStore(store)} data-testid="get-suburb-status-button">
                  Add
                </Button>
              </Grid>
            </Grid>
            <Grid container alignItems="center" spacing={2} py={2}>
              <Grid item xs={12}>
                <Paper sx={{ height: '60vh' }}>
                  {rows && (
                    <DataGrid
                      key={gridKey}
                      sortingOrder={['desc', 'asc']}
                      rows={rows}
                      columns={columns}
                      disableSelectionOnClick
                      disableColumnSelector
                      disableColumnFilter
                      sx={{
                        '.MuiDataGrid-columnSeparator': {
                          display: 'none',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#CD9BF0',
                          color: '#161616',
                        },
                        '& .MuiDataGrid-row': {
                          '&:hover': {
                            backgroundColor: '#faf2ff',
                          },
                        },
                      }}
                    />
                  )}
                </Paper>
              </Grid>
              <Grid item>
                <Button variant="contained" onClick={handleSave} disabled={!unsavedChanges}>
                  Save
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
